// components/ui/Icon.tsx
// -----------------------------------------------------------------------------
// FontAwesome icon wrapper component for consistent icon usage across the app.
// Uses the same icon naming convention as the mobile app (regular icons by default).
// -----------------------------------------------------------------------------

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";

// Free regular icons
import {
  faHeart as faHeartRegular,
  faComment as faCommentRegular,
  faCalendar as faCalendarRegular,
  faBell as faBellRegular,
  faUser as faUserRegular,
  faCircle,
  faCircleXmark,
  faCircleQuestion,
} from "@fortawesome/free-regular-svg-icons";

// Solid icons (only for filled/active states)
import {
  faHeart as faHeartSolid,
  faThumbtack as faThumbtackSolid,
  faBell as faBellSolid,
  faCircleCheck as faCircleCheckSolid,
  faChartSimple as faChartSimpleSolid,
} from "@fortawesome/free-solid-svg-icons";

// Brand icons
import {
  faInstagram,
  faTiktok,
  faStrava,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";

// Pro regular icons (matching mobile's "far" convention)
import { faMemo as farMemo } from "@fortawesome/pro-regular-svg-icons/faMemo";
import { faBolt as farBolt } from "@fortawesome/pro-regular-svg-icons/faBolt";
import { faCalendarPlus as farCalendarPlus } from "@fortawesome/pro-regular-svg-icons/faCalendarPlus";
import { faUserGroup as farUserGroup } from "@fortawesome/pro-regular-svg-icons/faUserGroup";
import { faCalendar as farCalendar } from "@fortawesome/pro-regular-svg-icons/faCalendar";
import { faComment as farComment } from "@fortawesome/pro-regular-svg-icons/faComment";
import { faUser as farUser } from "@fortawesome/pro-regular-svg-icons/faUser";
import { faGear as farGear } from "@fortawesome/pro-regular-svg-icons/faGear";
import { faSearch as farSearch } from "@fortawesome/pro-regular-svg-icons/faSearch";
import { faPlus as farPlus } from "@fortawesome/pro-regular-svg-icons/faPlus";
import { faCirclePlus as farCirclePlus } from "@fortawesome/pro-regular-svg-icons/faCirclePlus";
import { faCircleCheck as farCircleCheck } from "@fortawesome/pro-regular-svg-icons/faCircleCheck";
import { faXmark as farXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { faCheck as farCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faArrowLeft as farArrowLeft } from "@fortawesome/pro-regular-svg-icons/faArrowLeft";
import { faAngleRight as farAngleRight } from "@fortawesome/pro-regular-svg-icons/faAngleRight";
import { faAngleLeft as farAngleLeft } from "@fortawesome/pro-regular-svg-icons/faAngleLeft";
import { faAngleDown as farAngleDown } from "@fortawesome/pro-regular-svg-icons/faAngleDown";
import { faChevronDown as farChevronDown } from "@fortawesome/pro-regular-svg-icons/faChevronDown";
import { faGlobe as farGlobe } from "@fortawesome/pro-regular-svg-icons/faGlobe";
import { faLocationDot as farLocationDot } from "@fortawesome/pro-regular-svg-icons/faLocationDot";
import { faClock as farClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import { faEllipsis as farEllipsis } from "@fortawesome/pro-regular-svg-icons/faEllipsis";
import { faImage as farImage } from "@fortawesome/pro-regular-svg-icons/faImage";
import { faLink as farLink } from "@fortawesome/pro-regular-svg-icons/faLink";
import { faTrash as farTrash } from "@fortawesome/pro-regular-svg-icons/faTrash";
import { faPaperPlane as farPaperPlane } from "@fortawesome/pro-regular-svg-icons/faPaperPlane";
import { faShare as farShare } from "@fortawesome/pro-regular-svg-icons/faShare";
import { faPlay as farPlay } from "@fortawesome/pro-regular-svg-icons/faPlay";
import { faCirclePlay as farCirclePlay } from "@fortawesome/pro-regular-svg-icons/faCirclePlay";
import { faCircleArrowUp as farCircleArrowUp } from "@fortawesome/pro-regular-svg-icons/faCircleArrowUp";
import { faHouse as farHouse } from "@fortawesome/pro-regular-svg-icons/faHouse";
import { faBriefcase as farBriefcase } from "@fortawesome/pro-regular-svg-icons/faBriefcase";
import { faCake as farCake } from "@fortawesome/pro-regular-svg-icons/faCake";
import { faGraduationCap as farGraduationCap } from "@fortawesome/pro-regular-svg-icons/faGraduationCap";
import { faPhone as farPhone } from "@fortawesome/pro-regular-svg-icons/faPhone";
import { faEnvelope as farEnvelope } from "@fortawesome/pro-regular-svg-icons/faEnvelope";
import { faPenToSquare as farPenToSquare } from "@fortawesome/pro-regular-svg-icons/faPenToSquare";
import { faPoll as farPoll } from "@fortawesome/pro-regular-svg-icons/faPoll";
import { faLocationArrow as farLocationArrow } from "@fortawesome/pro-regular-svg-icons/faLocationArrow";
import { faLocationPin as farLocationPin } from "@fortawesome/pro-regular-svg-icons/faLocationPin";
import { faMapPin as farMapPin } from "@fortawesome/pro-regular-svg-icons/faMapPin";
import { faExclamation as farExclamation } from "@fortawesome/pro-regular-svg-icons/faExclamation";
import { faPen as farPen } from "@fortawesome/pro-regular-svg-icons/faPen";
import { faFlag as farFlag } from "@fortawesome/pro-regular-svg-icons/faFlag";
import { faAt as farAt } from "@fortawesome/pro-regular-svg-icons/faAt";
import { faCirclesOverlap as farCirclesOverlap } from "@fortawesome/pro-regular-svg-icons/faCirclesOverlap";
import { faThumbtack as farThumbtack } from "@fortawesome/pro-regular-svg-icons/faThumbtack";
import { faThumbtackSlash as farThumbtackSlash } from "@fortawesome/pro-regular-svg-icons/faThumbtackSlash";
import { faArrowUpFromBracket as farArrowUpFromBracket } from "@fortawesome/pro-regular-svg-icons/faArrowUpFromBracket";
import { faCreditCard as farCreditCard } from "@fortawesome/pro-regular-svg-icons/faCreditCard";
import { faCircleMinus as farCircleMinus } from "@fortawesome/pro-regular-svg-icons/faCircleMinus";
import { faArrowUpRightFromSquare as farArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons/faArrowUpRightFromSquare";
import { faScroll as farScroll } from "@fortawesome/pro-regular-svg-icons/faScroll";
import { faArrowRightFromBracket as farArrowRightFromBracket } from "@fortawesome/pro-regular-svg-icons/faArrowRightFromBracket";
import { faRectangleHistory as farRectangleHistory } from "@fortawesome/pro-regular-svg-icons/faRectangleHistory";
import { faHandWave as farHandWave } from "@fortawesome/pro-regular-svg-icons/faHandWave";
import { faHourglassHalf as farHourglassHalf } from "@fortawesome/pro-regular-svg-icons/faHourglassHalf";
import { faCalendarDay as farCalendarDay } from "@fortawesome/pro-regular-svg-icons/faCalendarDay";
import { faSignalStream as farSignalStream } from "@fortawesome/pro-regular-svg-icons/faSignalStream";
import { faCopy as farCopy } from "@fortawesome/pro-regular-svg-icons/faCopy";
import { faShield as farShield } from "@fortawesome/pro-regular-svg-icons/faShield";
import { faShieldCheck as farShieldCheck } from "@fortawesome/pro-regular-svg-icons/faShieldCheck";
import { faFileLines as farFileLines } from "@fortawesome/pro-regular-svg-icons/faFileLines";
import { faUsers as farUsers } from "@fortawesome/pro-regular-svg-icons/faUsers";
import { faCircleDot as farCircleDot } from "@fortawesome/pro-regular-svg-icons/faCircleDot";
import { faBell as farBell } from "@fortawesome/pro-regular-svg-icons/faBell";
import { faArrowDownToBracket as farArrowDownToBracket } from "@fortawesome/pro-regular-svg-icons/faArrowDownToBracket";
import { faEnvelopeOpenText as farEnvelopeOpenText } from "@fortawesome/pro-regular-svg-icons/faEnvelopeOpenText";
import { faArrowRotateRight as farArrowRotateRight } from "@fortawesome/pro-regular-svg-icons/faArrowRotateRight";
import { faUserCheck as farUserCheck } from "@fortawesome/pro-regular-svg-icons/faUserCheck";
import { faCircleExclamation as farCircleExclamation } from "@fortawesome/pro-regular-svg-icons/faCircleExclamation";
import { faFileImport as farFileImport } from "@fortawesome/pro-regular-svg-icons/faFileImport";
import { faFileArrowUp as farFileArrowUp } from "@fortawesome/pro-regular-svg-icons/faFileArrowUp";
import { faUserPlus as farUserPlus } from "@fortawesome/pro-regular-svg-icons/faUserPlus";

// Icon map for easy lookup by name
const iconMap: Record<string, IconProp> = {
  // Navigation (regular by default, matching mobile)
  "user-group": farUserGroup,
  "calendar": farCalendar,
  "calendar-outline": faCalendarRegular,
  "comment": farComment,
  "comment-outline": faCommentRegular,
  "user": farUser,
  "user-outline": faUserRegular,
  "gear": farGear,
  "search": farSearch,

  // Actions
  "heart": faHeartSolid,
  "heart-outline": faHeartRegular,
  "thumbtack": faThumbtackSolid,
  "plus": farPlus,
  "circle-plus": farCirclePlus,
  "circle-check": farCircleCheck,
  "check-circle": faCircleCheckSolid,
  "chart-simple": faChartSimpleSolid,
  "circle-xmark": faCircleXmark,
  "circle-question": faCircleQuestion,
  "circle": faCircle,
  "xmark": farXmark,
  "close": farXmark,
  "check": farCheck,

  // Navigation arrows
  "arrow-left": farArrowLeft,
  "angle-right": farAngleRight,
  "angle-left": farAngleLeft,
  "angle-down": farAngleDown,
  "chevron-down": farChevronDown,

  // Content
  "globe": farGlobe,
  "location-dot": farLocationDot,
  "clock": farClock,
  "ellipsis": farEllipsis,
  "image": farImage,
  "link": farLink,
  "trash": farTrash,
  "paper-plane": farPaperPlane,
  "share": farShare,
  "play": farPlay,
  "circle-play": farCirclePlay,
  "arrow-down-to-bracket": farArrowDownToBracket,

  // Notifications
  "bell": faBellSolid,
  "bell-outline": faBellRegular,

  // Send/Submit
  "circle-arrow-up": farCircleArrowUp,

  // Profile details
  "house": farHouse,
  "briefcase": farBriefcase,
  "cake": farCake,
  "graduation-cap": farGraduationCap,
  "phone": farPhone,
  "envelope": farEnvelope,

  // Social/Brand
  "instagram": faInstagram,
  "tiktok": faTiktok,
  "strava": faStrava,
  "linkedin": faLinkedin,

  // FAB/Action icons (using Pro regular icons to match mobile)
  "bolt": farBolt,
  "calendar-plus": farCalendarPlus,
  "memo": farMemo,
  "pen-to-square": farPenToSquare,
  "poll": farPoll,
  "location-arrow": farLocationArrow,
  "location-pin": farLocationPin,
  "map-pin": farMapPin,
  "exclamation": farExclamation,
  "pen": farPen,
  "flag": farFlag,
  "at": farAt,
  "circles": farCirclesOverlap,
  "thumbtack-outline": farThumbtack,
  "thumbtack-slash": farThumbtackSlash,
  "arrow-up-from-bracket": farArrowUpFromBracket,
  "credit-card": farCreditCard,
  "circle-minus": farCircleMinus,
  "arrow-up-right-from-square": farArrowUpRightFromSquare,
  "scroll": farScroll,
  "arrow-right-from-bracket": farArrowRightFromBracket,
  "rectangle-history": farRectangleHistory,
  "hand-wave": farHandWave,
  "hourglass-half": farHourglassHalf,
  "calendar-day": farCalendarDay,
  "signal-stream": farSignalStream,
  "copy": farCopy,
  "shield": farShield,
  "shield-check": farShieldCheck,
  "file-lines": farFileLines,
  "users": farUsers,
  "circle-dot": farCircleDot,
  "bell-regular": farBell,
  "envelope-open-text": farEnvelopeOpenText,
  "arrow-rotate-right": farArrowRotateRight,
  "user-check": farUserCheck,
  "circle-exclamation": farCircleExclamation,
  "file-import": farFileImport,
  "file-arrow-up": farFileArrowUp,
  "user-plus": farUserPlus,
};

export interface IconProps {
  /** Icon name from the iconMap */
  name: keyof typeof iconMap | string;
  /** Size in pixels (default: 16) */
  size?: number;
  /** Custom class name */
  className?: string;
  /** Color (uses currentColor by default) */
  color?: string;
}

/**
 * Icon component that wraps FontAwesome for consistent icon usage.
 * Uses the same naming convention as the mobile app for easy parity.
 */
export function Icon({ name, size = 16, className = "", color }: IconProps) {
  const icon = iconMap[name];

  if (!icon) {
    console.warn(`Icon "${name}" not found in icon map`);
    return null;
  }

  return (
    <FontAwesomeIcon
      icon={icon}
      style={{
        width: size,
        height: size,
        ...(color ? { color } : {}),
      }}
      className={className}
    />
  );
}

/**
 * Get an icon from the map for direct FontAwesomeIcon usage
 */
export function getIcon(name: keyof typeof iconMap): IconProp | undefined {
  return iconMap[name];
}

export default Icon;
